/*
 * dg_state, the one first-party cookie both hosts share. Owner spec, 25 Sep 2026.
 *
 *   name     dg_state
 *   scope    Domain=.daisysguide.com; Path=/; Secure; SameSite=Lax. Not HttpOnly:
 *            it is read by script on both hosts, which is its whole purpose.
 *   value    URL-encoded JSON, and only this:
 *              {"v":1,
 *               "cart":{"n":<item count>,"exp":<unix seconds>},
 *               "welcome":{"s":"dismissed"|"submitted","until":<unix seconds>}}
 *   expiry   PER FIELD. cart lives one day, welcome thirty. One cookie has one
 *            expiry, so Max-Age is the latest field still live, and a reader
 *            ignores any field whose own time has passed.
 *   category CookieYes Functional. Written only while CookieYes reports
 *            functional consent, and removed when it is withdrawn.
 *
 * WHAT THE THEME WRITES. cart.n, from the real cart: the server-rendered count
 * on every page load and each /cart/*.js response. welcome is carried through untouched on cart writes. dg-welcome-state.js
 * writes the existing welcome field for the PDP popup.
 *
 * WHAT IT NEVER HOLDS: an email, a stage, an answer, an identifier or a token.
 * Written from theme script only. The custom pixel does not read or write it,
 * and no advertising destination is sent it.
 */
(() => {
  const NAME = 'dg_state';
  const DAY = 86400;
  const APEX = 'daisysguide.com';
  const now = () => Math.floor(Date.now() / 1000);

  // On a preview host (*.myshopify.com, shopifypreview.com) a Domain attribute
  // for daisysguide.com would be rejected silently, so the cookie is host-only
  // there and shared only where it can be.
  const host = window.location.hostname;
  const domain = host === APEX || host.endsWith(`.${APEX}`) ? `; Domain=.${APEX}` : '';
  const attrs = `${domain}; Path=/; Secure; SameSite=Lax`;

  function read() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)dg_state=([^;]*)/);
      return match ? JSON.parse(decodeURIComponent(match[1])) : null;
    } catch {
      return null;
    }
  }

  function remove() {
    document.cookie = `${NAME}=${attrs}; Max-Age=0`;
  }

  function write(state) {
    const t = now();
    const live = [state.cart?.exp, state.welcome?.until].filter((at) => Number.isFinite(at) && at > t);
    if (live.length === 0) return remove();
    const value = encodeURIComponent(JSON.stringify(state));
    document.cookie = `${NAME}=${value}${attrs}; Max-Age=${Math.max(...live) - t}`;
  }

  function functionalConsent() {
    try {
      return Boolean(window.getCkyConsent?.().categories?.functional);
    } catch {
      return false;
    }
  }

  function writeCart(count) {
    if (!functionalConsent()) return;
    const n = Number.parseInt(count, 10);
    if (!Number.isFinite(n) || n < 0) return;
    const previous = read();
    const state = { v: 1, cart: { n, exp: now() + DAY } };
    const welcome = previous?.welcome;
    if (welcome && Number.isFinite(welcome.until) && welcome.until > now()
        && (welcome.s === 'dismissed' || welcome.s === 'submitted')) {
      state.welcome = { s: welcome.s, until: welcome.until };
    }
    write(state);
  }

  const pageCount = () => document.querySelector('[data-dg-cart-count]')?.getAttribute('data-dg-cart-count');

  // CookieYes dispatches this on every consent change, with the categories
  // accepted and rejected.
  document.addEventListener('cookieyes_consent_update', (event) => {
    const { accepted = [], rejected = [] } = event.detail ?? {};
    if (rejected.includes('functional')) remove();
    else if (accepted.includes('functional')) writeCart(pageCount());
  });

  window.DGState = { writeCart };
  writeCart(pageCount());
})();
